import React, { useEffect, useRef, useState } from 'react';
import QuickMenu from '../../components/QuickMenu/QuickMenu';

const rawApiUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
const API_URL = rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl.replace(/\/$/, '')}/api`;

const readJsonResponse = async (response) => {
  const text = await response.text();
  if (!text) {
    throw new Error('API boş yanıt döndürdü.');
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error('API JSON formatında yanıt döndürmedi.');
  }
};

const initialForm = {
  firstName: '',
  lastName: '',
  phone: '',
  neighborhoodGuid: '',
  addressDetail: '',
  eventDescription: '',
  departmentGuid: '',
  consentAccepted: false
};

const ReportIncident = () => {
  const [formData, setFormData] = useState(initialForm);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [optionsError, setOptionsError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const successMessageRef = useRef(null);

  const MAX_FILE_SIZE = 25 * 1024 * 1024;
  const MAX_FILES = 5;
  const MAX_DESCRIPTION_LENGTH = 1500;

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [departmentResponse, locationResponse] = await Promise.all([
          fetch(`${API_URL}/ReportIncident/departments`),
          fetch(`${API_URL}/ReportIncident/locations`)
        ]);
        const departmentResult = await readJsonResponse(departmentResponse);
        const locationResult = await readJsonResponse(locationResponse);

        if (!departmentResponse.ok || !locationResponse.ok) {
          throw new Error(departmentResult?.resultMessage || locationResult?.resultMessage || 'Seçenekler yüklenemedi.');
        }

        if (departmentResult?.isSuccess && Array.isArray(departmentResult.data)) {
          setDepartments(departmentResult.data);
        }

        if (locationResult?.isSuccess && Array.isArray(locationResult.data)) {
          setLocations(locationResult.data);
        }
      } catch (error) {
        setOptionsError(`Birim ve konum bilgileri yüklenemedi: ${error.message}`);
      }
    };

    loadOptions();
  }, []);

  useEffect(() => {
    if (isSuccess) {
      successMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isSuccess]);

  const selectedDepartment = departments.find((item) => item.guid === formData.departmentGuid);
  const selectedNeighborhood = locations.find((item) => item.guid === formData.neighborhoodGuid);

  const normalizePhone = (value) => {
    const digits = value.replace(/\D/g, '');
    const withoutLeadingZeros = digits.replace(/^0+/, '');
    return `0${withoutLeadingZeros}`.slice(0, 11);
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateFile = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/webm',
      'video/quicktime',
      'video/x-msvideo'
    ];

    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Sadece fotoğraf veya video dosyası yüklenebilir.' };
    }

    if (file.size > MAX_FILE_SIZE) {
      return { isValid: false, error: 'Her dosya en fazla 25 MB olabilir.' };
    }

    return { isValid: true };
  };

  const processFiles = (incomingFiles) => {
    const validFiles = [];
    const rejectedFiles = [];

    incomingFiles.forEach((file) => {
      const validation = validateFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        rejectedFiles.push(`${file.name}: ${validation.error}`);
      }
    });

    if (rejectedFiles.length > 0) {
      alert(rejectedFiles.join('\n'));
    }

    const remainingSlots = MAX_FILES - files.length;
    setFiles((prev) => [...prev, ...validFiles.slice(0, remainingSlots)]);

    if (validFiles.length > remainingSlots) {
      alert(`En fazla ${MAX_FILES} dosya ekleyebilirsiniz.`);
    }
  };

  const handleFileSelect = (e) => {
    processFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    processFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'Ad alanı zorunludur.';
    if (!formData.lastName.trim()) newErrors.lastName = 'Soyad alanı zorunludur.';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefon alanı zorunludur.';
    } else if (!/^0\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Telefon numarası 0 ile başlayan 11 rakam olmalıdır.';
    }
    if (!formData.neighborhoodGuid) newErrors.neighborhoodGuid = 'Mahalle seçimi zorunludur.';
    if (!formData.departmentGuid) newErrors.departmentGuid = 'Birim seçimi zorunludur.';
    if (!formData.eventDescription.trim()) {
      newErrors.eventDescription = 'Olay açıklaması zorunludur.';
    } else if (formData.eventDescription.trim().length < 10) {
      newErrors.eventDescription = 'Olay açıklaması en az 10 karakter olmalıdır.';
    }
    if (!formData.consentAccepted) {
      newErrors.consentAccepted = 'Geri dönüş için kişisel bilgilerinizin kullanılacağını onaylamanız gereklidir.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !validateForm()) return;

    const payload = new FormData();
    payload.append('FirstName', formData.firstName.trim());
    payload.append('LastName', formData.lastName.trim());
    payload.append('Phone', formData.phone.trim());
    payload.append('EventLocation', `${selectedNeighborhood?.name || ''} ${formData.addressDetail.trim()}`.trim());
    payload.append('NeighborhoodGuid', formData.neighborhoodGuid);
    payload.append('NeighborhoodName', selectedNeighborhood?.name || '');
    payload.append('AddressDetail', formData.addressDetail.trim());
    payload.append('EventDescription', formData.eventDescription.trim());
    payload.append('DepartmentGuid', formData.departmentGuid);
    payload.append('DepartmentName', selectedDepartment?.name || '');
    payload.append('ConsentAccepted', String(formData.consentAccepted));
    files.forEach((file) => payload.append('files', file));

    try {
      setIsSubmitting(true);
      const response = await fetch(`${API_URL}/ReportIncident`, {
        method: 'POST',
        body: payload
      });
      const result = await readJsonResponse(response);

      if (!response.ok || !result?.isSuccess) {
        throw new Error(result?.resultMessage || 'Bildirim gönderilemedi.');
      }

      setIsSuccess(true);
      setFormData(initialForm);
      setFiles([]);
      setErrors({});
    } catch (error) {
      alert(`Gönderim sırasında hata oluştu: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-content">
        <QuickMenu />

        <main className="main-content">
          <h2 className="page-title">Bize Bildir</h2>
          <div className="content-wrapper">
            {isSuccess && (
              <div ref={successMessageRef} className="report-success">
                Bildiriminiz alınmıştır. İlgili birim tarafından incelenecektir.
              </div>
            )}
            {optionsError && <div className="report-error">{optionsError}</div>}

            <form onSubmit={handleSubmit} className="report-form">
              <div className="report-grid">
                <div className="form-group">
                  <label htmlFor="firstName">Ad *</label>
                  <input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => updateField('firstName', e.target.value)}
                    className={errors.firstName ? 'error' : ''}
                  />
                  {errors.firstName && <small className="error-text">{errors.firstName}</small>}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Soyad *</label>
                  <input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    className={errors.lastName ? 'error' : ''}
                  />
                  {errors.lastName && <small className="error-text">{errors.lastName}</small>}
                </div>
              </div>

              <div className="report-grid">
                <div className="form-group">
                  <label htmlFor="phone">Telefon *</label>
                  <input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', normalizePhone(e.target.value))}
                    onFocus={() => !formData.phone && updateField('phone', '0')}
                    inputMode="numeric"
                    maxLength={11}
                    placeholder="05xxxxxxxxx"
                    className={errors.phone ? 'error' : ''}
                  />
                  {errors.phone && <small className="error-text">{errors.phone}</small>}
                </div>

                <div className="form-group">
                  <label htmlFor="departmentGuid">Birim *</label>
                  <select
                    id="departmentGuid"
                    value={formData.departmentGuid}
                    onChange={(e) => updateField('departmentGuid', e.target.value)}
                    className={errors.departmentGuid ? 'error' : ''}
                  >
                    <option value="">Birim seçiniz...</option>
                    {departments.map((department) => (
                      <option key={department.guid} value={department.guid}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                  {errors.departmentGuid && <small className="error-text">{errors.departmentGuid}</small>}
                </div>
              </div>

              <div className="report-grid">
                <div className="form-group">
                  <label htmlFor="neighborhoodGuid">Mahalle *</label>
                  <select
                    id="neighborhoodGuid"
                    value={formData.neighborhoodGuid}
                    onChange={(e) => updateField('neighborhoodGuid', e.target.value)}
                    className={errors.neighborhoodGuid ? 'error' : ''}
                  >
                    <option value="">Mahalle seçiniz...</option>
                    {locations.map((neighborhood) => (
                      <option key={neighborhood.guid} value={neighborhood.guid}>
                        {neighborhood.name}
                      </option>
                    ))}
                  </select>
                  {errors.neighborhoodGuid && <small className="error-text">{errors.neighborhoodGuid}</small>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="addressDetail">Adres Detayı</label>
                <input
                  id="addressDetail"
                  value={formData.addressDetail}
                  onChange={(e) => updateField('addressDetail', e.target.value)}
                  placeholder="Kapı no, bina, tarif veya ek bilgi"
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventDescription">
                  Olay *
                  <small>({formData.eventDescription.length}/{MAX_DESCRIPTION_LENGTH} karakter)</small>
                </label>
                <textarea
                  id="eventDescription"
                  rows={5}
                  value={formData.eventDescription}
                  onChange={(e) => updateField('eventDescription', e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
                  placeholder="Lütfen yaşanan durumu açık ve anlaşılır şekilde yazınız."
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  className={errors.eventDescription ? 'error' : ''}
                />
                {errors.eventDescription && <small className="error-text">{errors.eventDescription}</small>}
              </div>

              <div className="form-group">
                <label>
                  Fotoğraf / Video
                  <small>(En fazla {MAX_FILES} dosya, her biri max 25 MB)</small>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime,video/x-msvideo"
                  multiple
                  style={{ display: 'none' }}
                />
                <div
                  className={`file-drop-zone ${files.length >= MAX_FILES ? 'disabled' : ''}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => files.length < MAX_FILES && fileInputRef.current?.click()}
                >
                  <p>Dosyaları buraya sürükleyip bırakın veya seçmek için tıklayın.</p>
                  {files.length > 0 && (
                    <div className="file-list">
                      {files.map((file, index) => (
                        <div key={`${file.name}-${index}`} className="file-item">
                          <span>{file.name}</span>
                          <small>{(file.size / 1024 / 1024).toFixed(1)} MB</small>
                          <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(index); }}>
                            Kaldır
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <label className={`consent-box ${errors.consentAccepted ? 'error' : ''}`}>
                <input
                  type="checkbox"
                  checked={formData.consentAccepted}
                  onChange={(e) => updateField('consentAccepted', e.target.checked)}
                />
                <span>
                  Bildirimime geri dönüş yapılabilmesi için ad, soyad ve telefon bilgilerimin ilgili belediye birimi tarafından kullanılacağını kabul ediyorum.
                </span>
              </label>
              {errors.consentAccepted && <small className="error-text">{errors.consentAccepted}</small>}

              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Gönderiliyor...' : 'Bildirimi Gönder'}
              </button>
            </form>
          </div>
        </main>
      </div>

      <style jsx>{`
        .report-success {
          background: #ecfdf5;
          border: 1px solid #86efac;
          color: #166534;
          padding: 14px 16px;
          border-radius: 8px;
          margin-bottom: 18px;
          font-weight: 700;
        }
        .report-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
          padding: 14px 16px;
          border-radius: 8px;
          margin-bottom: 18px;
          font-weight: 700;
        }
        .report-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
        }
        .form-group {
          margin-bottom: 1.25rem;
        }
        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 700;
          color: #1f2937;
        }
        .form-group label small {
          font-weight: 500;
          color: #6b7280;
          margin-left: 8px;
        }
        input,
        select,
        textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          background: #fff;
        }
        textarea {
          resize: vertical;
        }
        input:focus,
        select:focus,
        textarea:focus {
          outline: none;
          border-color: #237cb2;
          box-shadow: 0 0 0 3px rgba(35, 124, 178, 0.14);
        }
        .error {
          border-color: #dc2626 !important;
        }
        .error-text {
          display: block;
          color: #dc2626;
          font-size: 0.875rem;
          margin-top: 6px;
        }
        .file-drop-zone {
          border: 2px dashed #cbd5e1;
          border-radius: 10px;
          padding: 22px;
          background: #f8fafc;
          cursor: pointer;
        }
        .file-drop-zone.disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
        .file-list {
          display: grid;
          gap: 8px;
          margin-top: 14px;
        }
        .file-item {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto auto;
          gap: 10px;
          align-items: center;
          padding: 10px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
        }
        .file-item span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .file-item button {
          border: 1px solid #fecaca;
          background: #fff1f2;
          color: #b91c1c;
          border-radius: 6px;
          padding: 6px 10px;
          font-weight: 700;
        }
        .consent-box {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          padding: 12px;
          border: 1px solid #dbeafe;
          border-radius: 8px;
          background: #eff6ff;
          color: #1e3a8a;
          margin-bottom: 16px;
        }
        .consent-box input {
          width: auto;
          margin-top: 4px;
        }
        .submit-button {
          width: 100%;
          padding: 14px 18px;
          border: 0;
          border-radius: 8px;
          background: #237cb2;
          color: #fff;
          font-weight: 800;
          font-size: 1rem;
        }
        .submit-button:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default ReportIncident;
